/**
 * Per-user appearance preference for the model-builder right dock:
 * text-size presets (applied via CSS zoom) and a high-contrast toggle
 * (applied via a data attribute consumed by styles.css).
 *
 * Persisted on the profile at `preferences.panelAppearance` (merge-safe:
 * reset writes defaults explicitly — the backend update is a deep merge,
 * so keys are never deleted). A module-global Zustand store gives every
 * consumer (panel root, portaled popovers, modals) the same optimistic
 * state the instant a chip is clicked; the profile round-trip follows in
 * the background.
 */
import { createContext, useCallback, useContext, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { create } from 'zustand';
import toast from 'react-hot-toast';
import { useAuth } from '@/auth/useAuth';
import { updateProfile } from '@/api/profiles';

export type PanelTextSize = 'default' | 'large' | 'xlarge';
export type PanelContrast = 'standard' | 'high';

export interface PanelAppearance {
  textSize: PanelTextSize;
  contrast: PanelContrast;
}

export const PANEL_APPEARANCE_DEFAULTS: PanelAppearance = {
  textSize: 'default',
  contrast: 'standard',
};

export const PANEL_ZOOM: Record<PanelTextSize, number> = {
  default: 1,
  large: 1.15,
  xlarge: 1.3,
};

export type PanelScopeProps = {
  style: CSSProperties;
  'data-pp-contrast': PanelContrast;
};

const useAppearanceStore = create<PanelAppearance>(() => ({
  ...PANEL_APPEARANCE_DEFAULTS,
}));

function normalize(raw: unknown): PanelAppearance {
  const o = (raw ?? {}) as Partial<PanelAppearance>;
  return {
    textSize: o.textSize === 'large' || o.textSize === 'xlarge' ? o.textSize : 'default',
    contrast: o.contrast === 'high' ? 'high' : 'standard',
  };
}

/** Guards the profile→store sync so a component mounting mid-save doesn't
 *  revert an optimistic update: only sync when the *server* value changed. */
let lastServerJson = '';

export function usePanelAppearance() {
  const { userProfile, refreshProfile } = useAuth();
  const { textSize, contrast } = useAppearanceStore();

  useEffect(() => {
    const server = normalize(userProfile?.preferences?.panelAppearance);
    const json = JSON.stringify(server);
    if (json !== lastServerJson) {
      lastServerJson = json;
      useAppearanceStore.setState(server);
    }
  }, [userProfile]);

  const save = useCallback(
    async (next: PanelAppearance) => {
      useAppearanceStore.setState(next);
      const id = userProfile?._id;
      if (!id) return;
      try {
        await updateProfile(id, {
          preferences: { ...userProfile?.preferences, panelAppearance: next },
        });
        await refreshProfile();
      } catch (err) {
        console.error('Failed to save panel appearance:', err);
        toast.error('Failed to save appearance preference');
      }
    },
    [userProfile, refreshProfile],
  );

  const zoom = PANEL_ZOOM[textSize];

  return {
    textSize,
    contrast,
    zoom,
    setTextSize: (v: PanelTextSize) => save({ textSize: v, contrast }),
    setContrast: (v: PanelContrast) => save({ textSize, contrast: v }),
    reset: () => save({ ...PANEL_APPEARANCE_DEFAULTS }),
    scopeProps: { style: { zoom }, 'data-pp-contrast': contrast } as PanelScopeProps,
  };
}

/** True when the component sits in the properties-panel React tree (context
 *  crosses portals, DOM does not — portaled popovers use this to decide
 *  whether the panel appearance applies to them). */
export const PanelScopeContext = createContext(false);
export function useInPanelScope(): boolean {
  return useContext(PanelScopeContext);
}

/**
 * Split a `usePopoverPosition`-style inline style into an unzoomed
 * positioning shell and a zoomed content style. CSS zoom multiplies every
 * length on the element it's set on — including its own left/top — so the
 * position must live on an outer element and the zoom on an inner one.
 * Numeric sizes are divided by the zoom so the *visual* size still matches
 * what the position math computed from viewport rects.
 */
export function splitPopoverStyle(
  style: CSSProperties,
  zoom: number,
): { shell: CSSProperties; content: CSSProperties } {
  const { position, left, top, zIndex, transform, width, maxHeight, minWidth, ...rest } = style;
  const scale = (v: number | string | undefined) =>
    typeof v === 'number' ? v / zoom : v;
  return {
    shell: { position, left, top, zIndex, transform },
    content: {
      ...rest,
      zoom,
      width: scale(width),
      maxHeight: scale(maxHeight),
      minWidth: scale(minWidth),
    },
  };
}
